import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { statsApi, submissionsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, Database } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

function fmt(v) {
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString('es-ES', { maximumFractionDigits: 2 });
}

export default function MyData() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    statsApi.company(user.id).then(r => setCompany(r.data)).catch(() => {});
    submissionsApi.mine().then(r => setSubs(r.data)).catch(() => {});
  }, [user.id]);

  if (!company) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-aea-blue" /></div>;

  const indicators = Object.keys(company.by_indicator || {});

  return (
    <div className="space-y-6 animate-fade-in-up">
      <Tabs defaultValue="evolution">
        <TabsList data-testid="mydata-tabs">
          <TabsTrigger value="evolution" data-testid="tab-evolution">Evolución</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="tab-submissions">Envíos realizados</TabsTrigger>
        </TabsList>

        <TabsContent value="evolution" className="mt-4">
          {indicators.length === 0 ? (
            <Card className="aea-card"><CardContent className="p-8 text-center text-slate-500"><Database className="w-10 h-10 mx-auto mb-3 opacity-40" />Aún no ha aportado datos.</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {indicators.map((code) => {
                const series = company.by_indicator[code].map(d => ({
                  label: `${d.period_label.slice(0,3)} ${d.year - 2000}`, value: d.value,
                }));
                const unit = company.by_indicator[code][0]?.unit || '';
                return (
                  <Card key={code} className="aea-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-outfit font-semibold">{code} <span className="text-slate-400 text-xs font-normal">({unit})</span></CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div style={{ width: '100%', height: 180 }}>
                        <ResponsiveContainer>
                          <LineChart data={series} margin={{ top: 4, right: 10, left: -5, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip formatter={(v) => fmt(v)} />
                            <Line type="monotone" dataKey="value" stroke="#0369A1" strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="submissions" className="mt-4">
          <Card className="aea-card">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Fecha</th>
                    <th className="text-left p-3">Periodo</th>
                    <th className="text-left p-3">Fichero</th>
                    <th className="text-left p-3">Estado</th>
                    <th className="text-right p-3">Válidas</th>
                    <th className="text-right p-3">Errores</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">Sin envíos aún</td></tr>}
                  {subs.map(s => (
                    <tr key={s.id} className="border-t" data-testid={`sub-row-${s.id}`}>
                      <td className="p-3 font-mono text-xs">{new Date(s.submitted_at).toLocaleDateString('es-ES')}</td>
                      <td className="p-3">{s.period_label}</td>
                      <td className="p-3 text-slate-600">{s.file_name}</td>
                      <td className="p-3"><span className={`status-badge status-${s.status === 'validada' ? 'valid' : s.status === 'error' ? 'invalid' : 'pending'}`}>{s.status}</span></td>
                      <td className="p-3 text-right font-mono">{s.valid_rows}</td>
                      <td className="p-3 text-right font-mono text-red-600">{s.error_rows}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
